import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormArray, FormBuilder, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import * as words from '../../services/language'
import { MessageService } from '../../services/message.service';
import { MasterService } from '../../services/master.service';
import { StoreService } from '../../services/store.service';
import { SecurityService } from '../../services/security.service';
import { Receivable } from 'src/app/models/receivable.model';
import { Payment } from 'src/app/models/payment.model';
import { PAYMENT_METHODS } from 'src/app/constants/payments';
import { formatCount, formatCurrency } from 'src/app/utils/format';
import { clean } from 'src/app/utils/numeric';
import { ago, daysAgo } from 'src/app/utils/date';
import moment from 'moment';
import { Kind } from 'src/app/models/kind.model';
import { Receipt } from 'src/app/models/receipt.model';
import { SAVE_DEBOUNCE_MILLIS } from 'src/app/constants/sale';

@Component({
    selector: 'app-payments',
    templateUrl: './payments.component.html',
    styleUrls: ['./payments.component.scss'],
    standalone: false
})
export class PaymentsComponent implements OnInit {

    /** Save the language */
    protected language: string= 'EN'

    /** Set all words */
    protected words = words.language

    /** Unique internal identity of the lot whose payments will be shown. */
    protected lotId: number = 0

    /** Unique internal identity of the sale whose payments will be shown. */
    protected saleId: number = 0
    
    /** Unique internal identity of the contact/customer whose payments will be shown. */
    protected contactId: number = 0

    /** Unique internal identity of the salle terms whose past due view will be loaded. */
    protected termsId: number = 0

    /** Details of the current state of the terms (receivable). */
    protected terms: Receivable;

    /** Unique internal identity of a receipt being viewed. */
    protected receiptId: number = 0

    /** The search form group */
    protected form: FormGroup | null = null

    /**  */
    protected paymentMethods: string[] = PAYMENT_METHODS

    /** Indicates the sales or contacts are being searched. */
    protected loading: boolean = false

    /** The current payment whose details are being viewed by the user. */
    protected detailIndex: number = 0;

    // A debounce timer for save events. Used to wait for
    // the user to stop typing before we save.
    private watcher: ReturnType<typeof setTimeout> | null = null

    /** */
    protected kinds: Object;

    /** Helper getter method to consistently get the list of form payments as an Array. */
    get payments() {
        return this.form.controls['payments'] as FormArray;
    }

    constructor(private route: ActivatedRoute, private ms: MessageService, private master: MasterService, private store: StoreService, protected security: SecurityService, private router: Router, private fb: FormBuilder) { 
        this.form = this.fb.group({
            payments: this.fb.array([])
        })
    }

    ngOnInit() {
        this.termsId = 0
        const id = parseInt(this.route.snapshot.params['id'])
        if (id && !isNaN(id) && (id > 0)) {
            this.termsId = id
            this.fetch()
            this.fetchPayments()
        }

        if (localStorage && localStorage.getItem('lot')) {
            this.lotId = parseInt(localStorage.getItem('lot'))
        }
    }

    // Listen to output of navbar
    protected listenerNav = (e): void => {
        switch (e['message']) {
            case 'changeLanguage':
                this.language = e['value']
                break
            case 'setPermissions':
                break
            case 'changeLot':                
                const id = parseInt(e.lot || 0)
                if (!id || isNaN(id) || (id <= 0)) {
                    return
                }

                if (this.lotId === id) {
                    return
                }

                this.lotId = id
                break
        }
    }

    /**
     * Get the details of the current terms (receivable) 
     * of the date the user is working from.
     */
    protected fetch = () => {
        if (!this.termsId || (this.termsId <= 0)) {
            return
        }

        const today:string = moment().startOf('day').format('YYYY-MM-DD')

        this.loading = true
        this.master.getAsync(`receivables/${this.termsId}?asOf=${today}`).then(res => {
            this.loading = false

            if (!res || !res.data) {
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error ? res.data.error : this.words[this.language]['apiNoResponse'] });
                return
            }

            if (!res.data || (!res.data.id)) {
                return
            }
            
            this.terms = Object.assign(new Receivable(), res.data)
            this.saleId = this.terms.saleId
            this.contactId = this.terms.contactId
        })
    }

    /**
     * Get a list of payments for the given terms (url param)
     */
    protected fetchPayments = () => {
        if (!this.termsId || (this.termsId <= 0)) {
            return
        }

        this.payments.clear()
        this.form.reset()

        this.loading = true
        this.master.getAsync(`payments/actual?term=${this.termsId}&notProgress=failed,discarded&types=payment,fee`).then(res => {
            this.loading = false

            if (!res || !res.data) {
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error ? res.data.error : this.words[this.language]['apiNoResponse'] });
                return
            }

            if (!res.data.payments || (res.data.payments.length <= 0)) {
                return
            }

            const payments: Payment[] = res.data.payments

            for (const payment of payments) {
                let daysLate = 0
                if (payment.daysLate > 0) {
                    daysLate = payment.daysLate
                }

                const item = this.fb.group({
                    id: new FormControl<number>(payment.id, []),
                    category: new FormControl<string>(payment.category.toLocaleLowerCase(), [Validators.required]),
                    method: new FormControl<string>(payment.method.toLocaleLowerCase(), [Validators.required]),
                    checkNumber: new FormControl<string>(payment.checkNumber, []),
                    paidIn100s: new FormControl<string>(formatCurrency(payment.paidIn100s || 0), []),
                    amount: new FormControl<string>(formatCurrency(payment.amount || 0), [Validators.required]),
                    amountDue: new FormControl<string>(formatCurrency(0), []),
                    refunded: new FormControl<string>(formatCurrency(payment.refunded || 0), []),
                    principal: new FormControl<string>(formatCurrency(payment.principal || 0), []),
                    interest: new FormControl<string>(formatCurrency(payment.interest || 0), []),
                    tax: new FormControl<string>(formatCurrency(payment.tax || 0), []),
                    beginBalance: new FormControl<string>(formatCurrency(payment.beginBalance || 0), []),
                    endBalance: new FormControl<string>(formatCurrency(payment.endBalance || 0), []),
                    payoff: new FormControl<string>(formatCurrency(0), []),
                    received: new FormControl<Date>(payment.received, [Validators.required]),
                    posted: new FormControl<Date>(payment.posted, []),
                    due: new FormControl<string>(moment(payment.due).format('MM/DD/YYYY'), []),
                    daysLate: new FormControl<string>(formatCount(daysLate), []),
                    lateFee: new FormControl<string>(formatCurrency(0), []),
                    receiptId: new FormControl<number>(payment.receiptId, []),
                    batch: new FormControl<string>(payment.batchId, []),
                    reference: new FormControl<string>(payment.referenceId, []),
                    auth: new FormControl<string>(payment.authId, []),
                    approval: new FormControl<string>(payment.approvalCode, []),
                    route: new FormControl<string | null>(null, []),
                    lastFour: new FormControl<string | null>(payment.lastFour, [])
                })

                this.payments.push(item)
            }
        })
    }

    /**
     * Add a new payment row to the form array
     * @param e Event that triggerd this method, if any
     */
    protected add = (e: Event): void => {
        const payment = this.fb.group({
            id: new FormControl<number>(0, []),
            category: new FormControl<string | null>(null, [Validators.required]),
            method: new FormControl<string>('unknown', [Validators.required]),
            checkNumber: new FormControl<string | null>(null, []),
            paidIn100s: new FormControl<string | null>(null, []),
            amount: new FormControl<string>(formatCurrency(0), [Validators.required]),
            amountDue: new FormControl<string>(formatCurrency(0), []),
            refunded: new FormControl<string>(formatCurrency(0), []),
            principal: new FormControl<string>(formatCurrency(0), []),
            interest: new FormControl<string>(formatCurrency(0), []),
            tax: new FormControl<string>(formatCurrency(0), []),
            beginBalance: new FormControl<string>(formatCurrency(0), []),
            endBalance: new FormControl<string>(formatCurrency(0), []),
            payoff: new FormControl<string>(formatCurrency(0), []),
            received: new FormControl<Date>(null, [Validators.required]),
            posted: new FormControl<Date>(null, []),
            due: new FormControl<Date>(null, []),
            daysLate: new FormControl<string>(formatCount(0), []),
            lateFee: new FormControl<string>(formatCurrency(0), []),
            receiptId: new FormControl<number>(0, []),
            batch: new FormControl<string | null>(null, []),
            reference: new FormControl<string | null>(null, []),
            auth: new FormControl<string | null>(null, []),
            approval: new FormControl<string | null>(null, []),
            route: new FormControl<string | null>(null, []),
            lastFour: new FormControl<string | null>(null, [])
        })

        this.payments.push(payment)
    }

    /**
     * Save a payment and apply it to the loan. Then recompute the 
     * receivables from that point forward.
     * @param index The index of the payment to save (apply)
     */
    protected save = (index: number): void => {
        if ((index < 0) || (index > this.payments.length - 1)) {
            return
        }

        const payment = this.payments.at(index)
        if (!payment || payment.invalid) {
            return
        }

        const amount = clean(payment.get('amount').value)
        if (amount <= 0) {
            return
        }

        const id = payment.get('id').value
        if (id && (id > 0)) {
            this.edit(index)
            return
        }

        const payload = {
            payments: [{
                saleId: this.saleId,
                paid: payment.get('received').value,
                source: 'editor',
                category: payment.get('category').value,
                amount: amount,
                type: payment.get('method').value,
                checkNumber: payment.get('checkNumber').value,
                paidIn100s: clean(payment.get('paidIn100s').value),
                batch: payment.get('batch').value || null,
                reference: payment.get('reference').value || null,
                auth: payment.get('auth').value || null,
                approval: payment.get('approval').value || null,
                route: payment.get('route').value || null,
                lastFour: payment.get('lastFour').value || null
            }]
        }

        this.master.postAsync(`payments/record`, payload).then(res => {
            this.loading = false

            if (!res || !res.data) {
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error ? res.data.error : this.words[this.language]['apiNoResponse'] });
                return
            }
            
            const receipts: Receipt[] = res.data
            if (!receipts || (receipts.length <= 0) || (!receipts[0].meta) || (receipts[0].meta.length <= 0)) {
                return
            }

            const receipt: Receipt = receipts[0]
            const meta: Payment = receipt.meta[0]

            payment.patchValue({
                id: meta.id,
                principal: formatCurrency(meta.principal),
                interest: formatCurrency(meta.interest),
                tax: formatCurrency(meta.tax),
                due: moment(meta.due).format('MM/DD/YYYY'),
                receiptId: receipt.id
            })

            payment.markAsPristine()
            this.fetch()
            this.sort()
        })
    }

    /**
     * Save all new and existing payments that have changed
     * and apply it to the loan. Then recompute the 
     * receivables from that point forward.
     */
    protected saveAll = (e: Event): void => {
        if (!this.payments || (this.payments.length <= 0)) {
            return
        }

        const payload = {
            payments: []
        }
        
        let index = 0
        for (const payment of this.payments.controls) {
            if (payment.pristine || payment.invalid) {
                index++
                continue
            }

            const amount = clean(payment.get('amount').value)
            if (amount <= 0) {
                index++
                continue
            }

            const id = payment.get('id').value
            if (id && (id > 0)) {
                this.edit(index)
                index++
                continue
            }

            payload.payments.push({
                saleId: this.saleId,
                paid: payment.get('received').value,
                source: 'editor',
                category: payment.get('category').value,
                amount: amount,
                type: payment.get('method').value,
                checkNumber: payment.get('checkNumber').value,
                paidIn100s: clean(payment.get('paidIn100s').value),
                batch: payment.get('batch').value || null,
                reference: payment.get('reference').value || null,
                auth: payment.get('auth').value || null,
                approval: payment.get('approval').value || null,
                route: payment.get('route').value || null,
                lastFour: payment.get('lastFour').value || null
            })

            index++
        }

        if (!payload || !payload.payments || (payload.payments.length <= 0)) {
            return
        }

        this.master.postAsync(`payments/record`, payload).then(res => {
            this.loading = false

            if (!res || !res.data) {
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error ? res.data.error : this.words[this.language]['apiNoResponse'] });
                return
            }
            
            const receipts: Receipt[] = res.data
            if (!receipts || (receipts.length <= 0)) {
                return
            }

            this.fetchPayments()
            this.fetch()            
        })
    }

    /**
     * Save edits for the payment at the given index. Then recompute the 
     * receivables from that point forward.
     * @param index The index of the payment to save (apply)
     */
    protected edit = (index: number): void => {
        if ((index < 0) || (index > this.payments.length - 1)) {
            return
        }

        const payment = this.payments.at(index)
        if (!payment || payment.invalid) {
            return
        }

        const amount = clean(payment.get('amount').value)
        if (amount <= 0) {
            return
        }

        const id = payment.get('id').value
        if (!id || (id <= 0)) {
            return
        }

        const payload = {
            received: payment.get('received').value,
            amount: amount,
            method: payment.get('method').value,
            checkNumber: payment.get('checkNumber').value,
            paidIn100s: clean(payment.get('paidIn100s').value),
            batchId: payment.get('batch').value || null,
            referenceId: payment.get('reference').value || null,
            authId: payment.get('auth').value || null,
            approvalCode: payment.get('approval').value || null,
            route: payment.get('route').value || null,
            lastFour: payment.get('lastFour').value || null
        }

        this.master.patch(`payments/actual/${id}`, payload, res => {
            this.loading = false

            if (!res || !res.data) {
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error ? res.data.error : this.words[this.language]['apiNoResponse'] });
                return
            }

            payment.markAsPristine()
            this.fetch()
            this.sort()
        })
    }

    /**
     * Remove a payment from the form array
     * @param index The index of the payment to remove
     */
    protected discard = (index: number): void => {
        if ((index < 0) || (index > this.payments.length - 1)) {
            return
        }

        const payment = this.payments.at(index)
        if (!payment) {
            return
        }

        const id = payment.get('id').value
        if (!id || (id <= 0)) {
            this.payments.removeAt(index)
            return
        }

        this.master.discard(`payments/actual/${id}`, res => {
            this.loading = false

            if (!res || !res.data) {
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error ? res.data.error : this.words[this.language]['apiNoResponse'] });
                return
            }

            this.payments.removeAt(index)
        })
    }

    /**
     * Remove a payment from the form array
     * @param index The index of the payment to remove
     */
    protected receipt = (index: number): void => {
        if ((index < 0) || (index > this.payments.length - 1)) {
            return
        }
        
        this.receiptId = parseInt(this.payments.at(index).get('receiptId').value)
        if (!this.receiptId || (this.receiptId <= 0)) {
            return
        }
    }

    /**
     * Listen for the user to close the receipt modal
     * @param {boolean} refunded Indicates whether or not the user decided to refund, part or whole, the payment.
     */
    protected receiptClosed = (refunded: boolean) => {
        if (refunded) {
        }

        this.receiptId = 0
    }

    /**
     * A payment received date was changed. If needed, read the 
     * receivable and update the payment details.
     * @param e Event that triggered the change.
     * @param index Index of the payment whose received date changed.
     */
    protected receivedChanged = (e: Event, index: number) => {
        if (this.watcher) {
            clearTimeout(this.watcher)
            this.watcher = null
        }

        if (!this.termsId || (this.termsId <= 0)) {
            return
        }

        if ((index < 0) || (index > this.payments.length - 1)) {
            return
        }
        
        const payment = this.payments.at(index)
        if (!payment) {
            return
        }

        const asOf = payment.get('received').value
        if (!asOf || (asOf.length <= 0)) {
            return
        }

        if (!moment(asOf).isValid()) {
            return
        }

        const ctx = this

        this.watcher = setTimeout(() => {
            this.master.getAsync(`receivables/${ctx.termsId}?asOf=${asOf}`).then(res => {
                if (!res || !res.data) {
                    return
                }

                if (res.status !== 200) {
                    ctx.ms.sendMessage("alert", { type: "danger", text: res.data.error ? res.data.error : ctx.words[ctx.language]['apiNoResponse'] });
                    return
                }

                const receivable: Receivable = res.data;
                if (!receivable || (!receivable.id)) {
                    return
                }

                ctx.appendKinds(asOf, receivable)
                const key = moment(asOf).format('YYYYMMDD')

                const kind: Kind = {
                    category: receivable.category,
                    due: receivable.due,
                    amount: receivable.amount
                }

                if (ctx.kinds && ctx.kinds[key] && (ctx.kinds[key].length > 0)) {
                    kind.category = ctx.kinds[key][0].category
                    kind.amount = ctx.kinds[key][0].amount
                    kind.due = ctx.kinds[key][0].due
                }

                let daysLate = moment(asOf).diff(kind.due, 'days')
                if (daysLate < 0) {
                    daysLate = 0
                }
                
                payment.patchValue({
                    due: moment(kind.due).format('MM/DD/YYYY'),
                    category: kind.category.toLocaleLowerCase(),
                    amountDue: formatCurrency(kind.amount),
                    amount: formatCurrency(kind.amount),
                    beginBalance:  formatCurrency(receivable.balance),
                    endBalance:  formatCurrency(receivable.balance),
                    payoff:  formatCurrency(receivable.payoff),
                    lateFee: formatCurrency(receivable.lateFees),
                    daysLate: daysLate
                })
            })

        }, SAVE_DEBOUNCE_MILLIS)
    }

    /**
     * Based on the given receivable build an array of payment 
     * kind (category) due date and amounts and append it to the kinds
     * for display in the UI.
     * @param asOf Date of the given receivable record to appened.
     * @param receivable Receivable meta data from the given asOf date.
     */
    private appendKinds = (asOf: string, receivable: Receivable) => {
        if (!asOf || !receivable || !receivable.id) {
            return
        }

        if (!this.kinds) {
            this.kinds = {}
        }

        const key = moment(asOf).format('YYYYMMDD')
        if (this.kinds[key]) {
            return
        }

        this.kinds[key] = []

        if (receivable.deposit > 0) {
            this.kinds[key].push({
                due: receivable.depositDue,
                category: 'deposit',
                amount: receivable.deposit
            })
        }

        if (receivable.initial > 0) {
            this.kinds[key].push({
                due: receivable.initialDue,
                category: 'initial down',
                amount: receivable.initial
            })
        }

        if (receivable.deferred > 0) {
            this.kinds[key].push({
                due: receivable.deferredDue,
                category: 'deferred down',
                amount: receivable.deferred
            })
        }

        if (!receivable.quote) {
            this.kinds[key].push({
                due: receivable.loanDue || receivable.due,
                category: receivable.bhph ? 'mv loan' : 'drive-out price',
                amount: receivable.loan
            })
        }

        if (receivable.lateFees > 0) {
            this.kinds[key].push({
                due: receivable.lateFeesDue,
                category: 'late fee',
                amount: receivable.lateFees
            })
        }

        if (receivable.cpi > 0) {
            this.kinds[key].push({
                due: receivable.cpiDue,
                category: 'cpi',
                amount: receivable.cpi
            })
        }
    }

    /**
     * Get a list of available payment kinds 
     * (categories) for the given payment 
     * received date.
     * @param received Date a payment is to be received.
     * @returns A string array of payment kinds (categories) that can be paid on the given date.
     */
    protected kindsAvailable = (received: string): string[] => {
        if (!received) {
            return []
        }

        const key = moment(received).format('YYYYMMDD')
        if (!this.kinds || !this.kinds[key]) {
            return []
        }

        const items = []
        for (const item of this.kinds[key]) {
            items.push(item.category)
        }

        return items
    }

    /**
     * A payment kind (category) was changed. Update the 
     * amount due and date due based on the selection.
     * @param e Event that triggered the change.
     * @param index Index of the payment whose payment kind (category) changed.
     */
    protected kindChanged = (e: Event, index: number) => {
        if (!this.termsId || (this.termsId <= 0)) {
            return
        }

        if ((index < 0) || (index > this.payments.length - 1)) {
            return
        }
        
        const payment = this.payments.at(index)
        if (!payment) {
            return
        }

        const asOf = payment.get('received').value
        if (!asOf || (asOf.length <= 0)) {
            return
        }

        const key = moment(asOf).format('YYYYMMDD')
        if (!this.kinds || !this.kinds[key]) {
            return
        }

        const selection = payment.get('category').value
        const kind: Kind = this.kinds[key].find((item) => item.category.toLocaleLowerCase() === selection)

        payment.patchValue({
            due: moment(kind.due).format('MM/DD/YYYY'),
            amountDue: formatCurrency(kind.amount),
            amount: formatCurrency(kind.amount)
        })
    }

    /**
     * 
     * @param e 
     * @param index 
     */
    protected details = (e: Event, index: number) => {
        if ((index < 0) || (index > this.payments.length - 1)) {
            return
        }

        this.detailIndex = index

        const btn = (document.getElementById('toggleDetailView') as HTMLButtonElement)
        if (!btn) {
            return
        }

        btn.click()
    }

    /**
     * Sort the payments form array by paid (received) 
     * date, typically after a save.
     */
    protected sort = () => {
        const sortables = this.payments
        if (!sortables || (sortables.length <= 0)) {
            return
        }

        this.form.get('payments').setValue(
            sortables.value.sort((left, right) => {
                const leftPaid = left['received']
                const rightPaid = right['received']

                if (leftPaid == rightPaid) {
                    return 0
                }

                const leftEmpty = (!leftPaid || (leftPaid.trim().length <= 0))
                const rightEmpty = (!rightPaid || (rightPaid.trim().length <= 0))

                if (leftEmpty && rightEmpty) {
                    return 0
                }

                if (leftEmpty && !rightEmpty) {
                    return 1
                }

                if (!leftEmpty && rightEmpty) {
                    return -1
                }

                if (moment(leftPaid).isAfter(rightPaid)) { // interchange them
                    return 1
                }

                return -1
            })
        )
    }

    protected dueToClass = (due: Date): string => {
        return ago(moment(due).format('YYYY-MM-DD'))
    }

    protected daysPastDue = (due: Date): number => {
        return daysAgo(moment(due).format('YYYY-MM-DD'))
    }
}
